import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "barte-design-system";
import { CreditCardIcon, PercentIcon } from "@phosphor-icons/react";

import { getLocalStorage } from "@shared/constants";
import { formatDelay } from "@shared/helpers";
import { useGetSellerPlan } from "@payfac/services";
import { getCompanyPlanContext, getCompanyPlanTaxByMcc } from "@payfac/helpers";
import { MCC_DEFAULT_KEY } from "@payfac/constants";
import { useGetCompanyPlans } from "@shared/services";
import type {
    IRatePlanTaxPix,
    IRatePlanTaxBankSlip,
    IRatePlanTaxDebit,
    IRatePlanTaxCredit,
} from "@payfac/types";
import { ICompanyPlanTaxMethodDetail, } from '@shared/types'
import { ERatePlanReceiptType, ERatePlanTax } from "@payfac/types";
import {
    CardInfo,
    ModalEditPix,
    ModalEditBoleto,
    ModalEditDebit,
    ModalEditCredit,
    ModalConfirmSave,
} from "..";
import { RatePlanParams } from "../rate-plan-params";
import { CardDebitCard, CardPix, CardBoleto } from "./components";
import type { RatePlanDetailsProps } from "./rate-plan-details.type";
import styles from "./rate-plan-details.module.css";

export const RatePlanDetails = ({
    data,
    isLoading,
    onSubmitData,
    isPending,
    isCreate,
}: RatePlanDetailsProps) => {
    const { t } = useTranslation();

    const [openModal, setOpenModal] = useState<string>("");
    const [pendingUpdate, setPendingUpdate] = useState<{
        type: keyof typeof ERatePlanTax;
        formData: (
            | IRatePlanTaxPix
            | IRatePlanTaxBankSlip
            | IRatePlanTaxDebit
            | IRatePlanTaxCredit[]
        ) & { description?: string };
        newReceiptType?: keyof typeof ERatePlanReceiptType;
    } | null>(null);
    const [receiptType, setReceiptType] = useState(data?.tax?.receiptType || "EARLY")

    useEffect(() => {
        if (data?.tax?.receiptType) {
            setReceiptType(data.tax.receiptType);
        }
    }, [data?.tax?.receiptType])

    const companyId = getLocalStorage("companyId");

    const [searchParams] = useSearchParams();
    const sellerId = searchParams.get("sellerId") ?? "";

    const { data: sellerPlan, isLoading: isLoadingSellerPlan } = useGetSellerPlan(
        {
            companyId,
            sellerId,
        },
    );

    const changedReceiptType = receiptType !== data?.tax?.receiptType

    const planRecord = sellerPlan?.data?.data?.[0];
    const registeredMcc = planRecord?.mcc;
    const mccLocked = !!sellerId;
    const effectiveMcc = mccLocked
        ? registeredMcc || MCC_DEFAULT_KEY
        : searchParams.get("mcc") ?? MCC_DEFAULT_KEY;

    const earlyPlanId = planRecord?.idPlanCompanyDefault;
    const timelinePlanId = planRecord?.idPlanCompanyTimeline;
    const sellerCompanyPlanId = sellerId ? planRecord?.idPlanCompany : null;

    const {
        data: earlyCompanyPlansDefault,
        isLoading: isLoadingEarlyCompanyPlan,
    } = useGetCompanyPlans({
        planId: earlyPlanId,
        companyId,
    });

    const {
        data: timelineCompanyPlansDefault,
        isLoading: isLoadingTimelineCompanyPlan,
    } = useGetCompanyPlans({
        planId: timelinePlanId,
        companyId,
    });

    const {
        data: sellerCompanyPlanResponse,
        isLoading: isLoadingSellerCompanyPlan,
    } = useGetCompanyPlans({
        planId: sellerCompanyPlanId,
        companyId,
    });

    const basePlanContext = useMemo(
        () =>
            getCompanyPlanContext({
                sellerCompanyPlans: sellerCompanyPlanResponse?.data,
                earlyCompanyPlans: earlyCompanyPlansDefault?.data,
                timelineCompanyPlans: timelineCompanyPlansDefault?.data,
                receiptType,
                receiptTypeChanged: changedReceiptType,
            }),
        [
            sellerCompanyPlanResponse,
            earlyCompanyPlansDefault,
            timelineCompanyPlansDefault,
            receiptType,
            changedReceiptType,
        ],
    );

    const availableMccs = useMemo(
        () => Object.keys(basePlanContext?.tax?.taxConfigsByMcc ?? {}),
        [basePlanContext],
    );

    const rawTaxAggregateVersion = isCreate
        ? sellerPlan?.data?.data?.[0]?.taxAggregateVersion ?? "2"
        : data?.tax_aggregate_version ?? "2";
    const taxAggregateVersion = String(rawTaxAggregateVersion) as "1" | "2";

    const isLoadingCredit =
        isLoadingSellerPlan ||
        (!!earlyPlanId && isLoadingEarlyCompanyPlan) ||
        (!!timelinePlanId && isLoadingTimelineCompanyPlan) ||
        (!!sellerCompanyPlanId && isLoadingSellerCompanyPlan);


    // without credit
    const getCompanyPlan = (
        type: Exclude<keyof typeof ERatePlanTax, "credit">,
    ): ICompanyPlanTaxMethodDetail | undefined => {
        const delayForRedeem = data?.tax?.[type]?.delayForRedeem;

        const companyPlanConfigs =
            getCompanyPlanTaxByMcc(basePlanContext, effectiveMcc)?.[type];

        if (companyPlanConfigs?.[delayForRedeem]) {
            return companyPlanConfigs?.[
                delayForRedeem
            ] as ICompanyPlanTaxMethodDetail;
        }

        return companyPlanConfigs?.[
            Object.keys(companyPlanConfigs || {})?.[0]
        ] as ICompanyPlanTaxMethodDetail;
    };

    const onSuccessRequest = () => {
        setOpenModal("");
        setPendingUpdate(null);
    };

    const handleUpdateSubmit = async (
        type: keyof typeof ERatePlanTax,
        formData:
            | IRatePlanTaxPix
            | IRatePlanTaxBankSlip
            | IRatePlanTaxDebit
            | IRatePlanTaxCredit[],
        newReceiptType?: keyof typeof ERatePlanReceiptType,
        description?: string,
    ) => {
        const payload = {
            ...data,
            title: data?.name, // isso aqui é loucura
            description: description || data?.name, // isso aqui é loucura
            tax: {
                ...data.tax,
                [type]: formData,
                ...(newReceiptType ? { receiptType: newReceiptType } : {}),
            },
        };

        onSubmitData(payload, onSuccessRequest);
    };

    const paramsBlock = (
        <RatePlanParams
            planId={Number(data?.id) || 0}
            availableMccs={availableMccs}
            registeredMcc={registeredMcc}
        />
    );

    return (
        <div className={styles.content}>
            {paramsBlock}

            <CardPix
                isLoading={isLoading}
                data={data?.tax?.pix}
                onEdit={() => setOpenModal("pix")}
            />

            <CardBoleto
                isLoading={isLoading}
                companyPlan={getCompanyPlan("bankSlip")}
                data={data?.tax?.bankSlip}
                onEdit={() => setOpenModal("boleto")}
            />

            <CardDebitCard
                isLoading={isLoading}
                companyPlan={getCompanyPlan("debit")}
                taxAggregateVersion={taxAggregateVersion}
                data={data?.tax?.debit}
                onEdit={() => setOpenModal("debit")}
            />

            <CardInfo
                title={
                    <span className={styles["card-title"]}>
                        <CreditCardIcon className={styles.icon} />{" "}
                        {t("payfac.pageRatePlanDetailsCreditCard")}
                    </span>
                }
                action={
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOpenModal("credit")}
                    >
                        {t("payfac.pageRatePlanDetailsEditButton")}
                    </Button>
                }
                isLoading={isLoading || isLoadingCredit}
                info={[
                    {
                        label: t("payfac.pageRatePlanDetailsReceiptType"),
                        value:
                            data?.tax?.receiptType === "TIMELINE"
                                ? t("payfac.pageRatePlanDetailsTimeline")
                                : t("payfac.pageRatePlanDetailsEarly"),
                    },
                    {
                        label: t("payfac.pageRatePlanDetailsRedeemTimeOnline"),
                        value: formatDelay(data?.tax?.credit?.[0]?.online?.delayForRedeem),
                    },
                    {
                        label: t("payfac.pageRatePlanDetailsRedeemTimePos"),
                        value: formatDelay(data?.tax?.credit?.[1]?.pos?.delayForRedeem),
                    },
                    {
                        label: t("payfac.pageRatePlanDetailsRedeemTimeTap"),
                        value: formatDelay(data?.tax?.credit?.[2]?.tap?.delayForRedeem),
                    },
                    {
                        label: t("payfac.pageRatePlanDetailsRates"),
                        value: (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setOpenModal("credit")}
                                iconLeft={<PercentIcon />}
                            >
                                {t("payfac.pageRatePlanDetailsEditRatesButton")}
                            </Button>
                        ),
                    },
                ]}
            />

            <ModalEditPix
                isOpen={openModal === "pix"}
                onClose={() => setOpenModal("")}
                data={data?.tax?.pix}
                onHandleSubmit={(formData) => handleUpdateSubmit("pix", formData)}
                isLoading={isPending}
                companyRate={getCompanyPlan("pix")?.value}
            />

            <ModalEditBoleto
                isOpen={openModal === "boleto"}
                onClose={() => setOpenModal("")}
                data={data?.tax?.bankSlip}
                onHandleSubmit={(formData) => handleUpdateSubmit("bankSlip", formData)}
                isLoading={isPending}
                companyRate={getCompanyPlan("bankSlip")?.value}
                companyRateType={getCompanyPlan("bankSlip")?.rateType === "FIXED" ? "FIXED" : "PERCENTAGE"}
            />

            <ModalEditDebit
                isOpen={openModal === "debit"}
                onClose={() => setOpenModal("")}
                data={data?.tax?.debit}
                onHandleSubmit={(formData) => handleUpdateSubmit("debit", formData)}
                isLoading={isPending}
                companyRate={getCompanyPlan("debit")?.value}
                taxAggregateVersion={taxAggregateVersion}
            />

            <ModalEditCredit
                isOpen={openModal === "credit"}
                onClose={() => setOpenModal("")}
                data={data?.tax?.credit}
                receiptType={receiptType}
                onChangeReceiptType={setReceiptType}
                receiptTypeChanged={receiptType !== data?.tax?.receiptType}
                onHandleSubmit={(formData, receiptTypeParam) =>
                    isCreate
                        ? handleUpdateSubmit("credit", formData, receiptTypeParam)
                        : setPendingUpdate({
                            type: "credit",
                            formData,
                            newReceiptType: receiptTypeParam,
                        })
                }
                isLoading={isPending}
                earlyCompanyPlans={earlyCompanyPlansDefault?.data}
                timelineCompanyPlans={timelineCompanyPlansDefault?.data}
                sellerCompanyPlans={sellerCompanyPlanResponse?.data}
                taxAggregateVersion={taxAggregateVersion}
                mcc={effectiveMcc}
                params={paramsBlock}
            />

            <ModalConfirmSave
                isOpen={!!pendingUpdate}
                onClose={() => setPendingUpdate(null)}
                onConfirm={(description) => {
                    if (pendingUpdate) {
                        handleUpdateSubmit(
                            pendingUpdate.type,
                            pendingUpdate.formData,
                            pendingUpdate.newReceiptType,
                            description,
                        );
                    }
                }}
                isLoading={isPending}
            />
        </div>
    );
};
